import { ContextWindow } from "@asup/context-menu";
import { isEqual } from "lodash";
import { ReactNode } from "react";
import { AitCommentRow } from "./AitCommentRow";
import { AitRowGroupReplacements } from "./AitRowGroupReplacements";
import { AitRowGroupSpaceAfter } from "./AitRowGroupSpaceAfter";
import { AitRowGroupData } from "./functions/AitRowGroupData";
import { getBaseId } from "./functions/getBaseId";
import { AitLocationBodyRowGroup, HIDE_OPTIONS_WINDOW } from "./functions/interface";
import { trGetPositionFromLocation } from "./functions/trGetPositionFromLocation";
import { useTableContext } from "./TableContextProvider";

interface AitRowGroupOptionsWindowProps {
  location: AitLocationBodyRowGroup;
}

/**
 * Options window for a row group (replacements, comments and spacing).
 *
 * @template T - Row group payload type
 * @param props - Component props
 * @param props.location - The row group location
 */
export const AitRowGroupOptionsWindow = <T,>({
  location,
}: AitRowGroupOptionsWindowProps): ReactNode => {
  const { state, dispatch } = useTableContext<T>();
  const id = getBaseId(location, state?.id);
  const thisRowGroup: AitRowGroupData<T> | undefined = trGetPositionFromLocation(
    state,
    location,
  ).data;
  const showRowGroupOptions =
    state && state.showOptionsWindow.findIndex((l) => isEqual(l, location)) > -1;

  return !thisRowGroup || !showRowGroupOptions ? null : (
    <ContextWindow
      id={`${id}-rowgroup-options-window`}
      title={"Row group options"}
      visible={showRowGroupOptions}
      onClose={() => {
        dispatch({
          operation: HIDE_OPTIONS_WINDOW,
          location,
        });
      }}
      style={{ maxHeight: "75vh" }}
    >
      <AitCommentRow location={location} />
      <AitRowGroupSpaceAfter location={location} />
      <AitRowGroupReplacements location={location} />
    </ContextWindow>
  );
};

AitRowGroupOptionsWindow.displayName = "AitRowGroupOptionsWindow";
